import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject } from 'rxjs';
import { ChatBotComponent } from '../components/chat-bot/chat-bot.component';

export interface PersistedChatState {
  isMinimized: boolean;
  sessionId: string;
  messages: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotOverlayService {

  private overlayRef!: OverlayRef;

  private chatOpenSubject = new BehaviorSubject<boolean>(false);
  chatOpen$ = this.chatOpenSubject.asObservable();

  private readonly STATE_KEY = 'iris_chat_state';
  private readonly REOPEN_KEY = 'iris_reopen_after_redirect';
  private restoreOnNextOpen = false;

  constructor(private overlay: Overlay, private injector: Injector) {}

  get isChatOpen(): boolean {
    return !!this.overlayRef;
  }

  openChatBot() {

    if (this.overlayRef) {
      return;
    }

    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      panelClass: 'chatbot-overlay-panel'
    });

    const portal = new ComponentPortal(ChatBotComponent);
    this.overlayRef.attach(portal);

    this.chatOpenSubject.next(true);
  }

  closeChatBot() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null!;
      this.chatOpenSubject.next(false);
    }
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  saveChatState(state: PersistedChatState): void {
    try {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded — silent fail */ }
  }

  getChatState(): PersistedChatState | null {
    try {
      const raw = localStorage.getItem(this.STATE_KEY);
      return raw ? (JSON.parse(raw) as PersistedChatState) : null;
    } catch { return null; }
  }

  clearChatState(): void {
    localStorage.removeItem(this.STATE_KEY);
  }

  // ── Redirect flag ─────────────────────────────────────────────────────────

  markReopenAfterRedirect(): void {
    localStorage.setItem(this.REOPEN_KEY, 'true');
  }

  shouldReopenAfterRedirect(): boolean {
    return localStorage.getItem(this.REOPEN_KEY) === 'true';
  }

  clearReopenFlag(): void {
    localStorage.removeItem(this.REOPEN_KEY);
  }

  // ── Restore flag (in-memory only) ─────────────────────────────────────────

  setRestoreOnNextOpen(): void {
    this.restoreOnNextOpen = true;
  }

  consumeRestoreOnNextOpen(): boolean {
    const val = this.restoreOnNextOpen;
    this.restoreOnNextOpen = false;
    return val;
  }
}